/**
 * FilterBar Component
 * Reusable filter UI for list views
 */

import { useCallback } from 'react';

export interface FilterField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date';
  placeholder?: string;
  options?: { value: string; label: string }[];
}

interface FilterBarProps {
  fields: FilterField[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  onSearch: () => void;
  onReset: () => void;
  isLoading?: boolean;
  className?: string;
}

export default function FilterBar({
  fields,
  values,
  onChange,
  onSearch,
  onReset,
  isLoading = false,
  className = '',
}: FilterBarProps) {
  const handleFieldChange = useCallback(
    (name: string, value: string) => {
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onSearch();
      }
    },
    [onSearch]
  );

  const handleReset = useCallback(() => {
    const resetValues: Record<string, string> = {};
    fields.forEach((field) => {
      resetValues[field.name] = '';
    });
    onChange(resetValues);
    onReset();
  }, [fields, onChange, onReset]);

  // 現在適用されているフィルター数をカウント
  const activeFilterCount = Object.values(values).filter(v => v && v.trim() !== '').length;

  return (
    <div className={`bg-gray-50 p-4 rounded-lg mb-4 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {fields.map((field) => (
          <div key={field.name}>
            <label
              htmlFor={`filter-${field.name}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.label}
            </label>
            {field.type === 'select' ? (
              <select
                id={`filter-${field.name}`}
                value={values[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">すべて</option>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'date' ? (
              <input
                type="date"
                id={`filter-${field.name}`}
                value={values[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            ) : (
              <input
                type="text"
                id={`filter-${field.name}`}
                value={values[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          <span>すべての条件に一致（AND検索） / 名前は部分一致</span>
          {activeFilterCount > 0 && (
            <span className="ml-3 text-blue-600 font-medium">
              {activeFilterCount}件のフィルター適用中
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-gray-600 bg-white border rounded-md hover:bg-gray-50"
            disabled={isLoading}
          >
            リセット
          </button>
          <button
            onClick={onSearch}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? '検索中...' : '検索'}
          </button>
        </div>
      </div>
    </div>
  );
}
